import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NoSectionAccess } from '../../components/NoSectionAccess/NoSectionAccess';
import { useAcombaBridgeAccessReady } from '../../hooks/useAcombaBridgeAccessReady';
import { useCanCollectPayment } from '../../hooks/useCanCollectPayment';
import { TranslationKey } from '../../i18n/translationKeys';
import { useRelanceDeactivationStore } from '../../stores/relanceDeactivationStore';
import { useTasksStore } from '../../stores/tasksStore';
import type { RelanceAgendaEntry } from '../../types/customerFollowUp/relanceAgenda';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import { PageTitle } from '../../ui/PageTitle/PageTitle';
import { RetainedContent } from '../../ui/RetainedContent/RetainedContent';
import { RelanceAgenda } from './RelanceAgenda';
import { RelanceAgendaSkeleton } from './RelanceAgendaSkeleton';

interface RelanceListPageProps {
    title: string;
    emptyText: string;
    entries: RelanceAgendaEntry[];
    showDates?: boolean;
    testIds: { title: string; noAccess: string; error: string; empty: string; list: string };
}

export const RelanceListPage: React.FC<RelanceListPageProps> = ({ title, emptyText, entries, showDates = false, testIds }) => {
    const { t } = useTranslation();
    const fetchDailyTasks = useTasksStore(s => s.fetchDailyTasks);
    // Fails open while the list has not loaded, like every other relance page.
    const relanceAccessible = useTasksStore(s => s.dailyTasks?.relanceAccessible ?? true);
    const deactivatedLoading = useRelanceDeactivationStore(s => s.deactivatedLoading);
    const deactivatedLoaded = useRelanceDeactivationStore(s => s.deactivatedLoaded);
    const error = useRelanceDeactivationStore(s => s.error);
    const fetchDeactivatedItems = useRelanceDeactivationStore(s => s.fetchDeactivatedItems);
    const canCollectPayment = useCanCollectPayment();
    // See useAcombaBridgeAccessReady: reading earlier can show the previous Usager's access and list.
    const bridgeAccessReady = useAcombaBridgeAccessReady();
    // The store's list may predate this page, so nothing is shown before this page's own fetch starts.
    const [fetchStarted, setFetchStarted] = useState(false);
    const loading = !fetchStarted || (deactivatedLoading && !deactivatedLoaded);

    useEffect(() => {
        if (!bridgeAccessReady) return;
        void fetchDailyTasks();
        void fetchDeactivatedItems();
        setFetchStarted(true);
    }, [bridgeAccessReady, fetchDailyTasks, fetchDeactivatedItems]);

    return (
        <div className="flex flex-col gap-2xl">
            <PageTitle data-testid={testIds.title}>{title}</PageTitle>
            {fetchStarted && !relanceAccessible ? (
                <NoSectionAccess
                    testId={testIds.noAccess}
                    title={t(TranslationKey.Home.NoSectionAccess.Title)}
                    message={t(TranslationKey.Home.NoSectionAccess.Message)}
                />
            ) : (
                <>
                    {loading ? (
                        <RelanceAgendaSkeleton />
                    ) : error ? (
                        <div className="flex flex-col gap-s">
                            <p data-testid={testIds.error} className="text-s text-danger">{t(TranslationKey.ApiErrors.Generic)}</p>
                            <LinkButton size="xs" data-testid={`${testIds.error}-retry`} onClick={() => void fetchDeactivatedItems()} className="w-fit">
                                {t(TranslationKey.Home.Agenda.Retry)}
                            </LinkButton>
                        </div>
                    ) : entries.length === 0 ? (
                        <p data-testid={testIds.empty} className="text-s text-primary-700">{emptyText}</p>
                    ) : null}
                    {/* Hidden, not unmounted, so a modal opened from the list keeps what was typed across a reload. */}
                    <RetainedContent hidden={loading || error != null || entries.length === 0}>
                        <RelanceAgenda entries={entries} canCollectPayment={canCollectPayment} showDates={showDates} testId={testIds.list} />
                    </RetainedContent>
                </>
            )}
        </div>
    );
};
